/**
 * Loan detail — read view + transition controls.
 *
 * Transitions are exposed contextually based on the loan's current status,
 * matching the legal-transition table in `@dfp/domain`. Sensitive
 * transitions (WriteOff, RaiseDispute, Lock) prompt for a reason; the
 * server's domain layer enforces the ≥10-char gate.
 *
 * Verification outcomes are NOT transitions here — a PENDING_VERIFICATION loan
 * shows the dedicated VerificationPanel (checklist + outcome + notes), which
 * posts to `POST /loans/:id/verification` under `loan.approve_verification`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import React, { useState } from "react";
import {
  ApiError,
  type Contract,
  type Customer,
  type Dealer,
  type Device,
  type Loan,
  type LoanTransitionRequest,
  type Payment,
} from "@dfp/api-client";
import { Badge } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Collapsible } from "../components/Collapsible.js";
import { ContractRequiredBanner } from "../components/ContractRequiredBanner.js";
import { LinkExistingPaymentForm } from "../components/LinkExistingPaymentForm.js";
import { RecordDepositForm } from "../components/RecordDepositForm.js";
import { RecordManualPaymentForm } from "../components/RecordManualPaymentForm.js";
import { RequestApprovalForm } from "../components/RequestApprovalForm.js";
import { ServicePinCard } from "../components/ServicePinCard.js";
import { OfflineUnlockCodeCard } from "../components/OfflineUnlockCodeCard.js";
import { Table, TBody, Td, Th, THead, Tr } from "../components/Table.js";
import { VerificationPanel } from "../components/VerificationPanel.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

interface TransitionDef {
  readonly label: string;
  readonly kind: LoanTransitionRequest["kind"];
  readonly needsReason?: boolean;
  readonly variant?: "primary" | "secondary" | "danger";
  /** Which statuses make this transition legal. */
  readonly allowedFrom: readonly Loan["status"][];
  /** Permission required to expose the button. */
  readonly permission: string;
}

/**
 * Window during which the undo banner remains visible after a
 * successful Lock or Unlock. 30 s is long enough for an operator to
 * realise the misclick + reach the mouse, short enough not to clutter
 * the page through the next operator action.
 */
const UNDO_WINDOW_MS = 30_000;

const TRANSITIONS: readonly TransitionDef[] = [
  {
    label: "Submit for verification",
    kind: "Submit",
    allowedFrom: ["DRAFT"],
    permission: "loan.write",
    variant: "primary",
  },
  // "Record deposit" used to be a TRANSITIONS entry that POSTed
  // /api/loans/:id/transitions {kind: "RecordDeposit"} — a state-only
  // flip with NO payment row. Removed: a deposit is a payment, so it
  // must be recorded via the sibling RecordDepositForm (or linked from
  // an existing one via LinkExistingPaymentForm). The server's
  // payments.manual + maybeAdvanceLoanOnDeposit path drives the same
  // PENDING_DEPOSIT → ACTIVE transition automatically once the
  // amount clears.
  {
    label: "Raise dispute",
    kind: "RaiseDispute",
    needsReason: true,
    allowedFrom: ["ACTIVE", "IN_ARREARS", "LOCKED"],
    permission: "loan.write",
    variant: "secondary",
  },
  {
    label: "Resolve dispute",
    kind: "ResolveDispute",
    allowedFrom: ["IN_DISPUTE"],
    permission: "loan.write",
    variant: "primary",
  },
  {
    label: "Lock",
    kind: "Lock",
    // Manual operator-issued Lock. Same dispatch shape as Unlock — its
    // own /loans/:id/lock endpoint (NOT the generic /transitions path)
    // because the lock-control service has to drive the provider adapter
    // synchronously to queue the device command. ACTIVE / IN_ARREARS only
    // — pre-signing loans don't have a device leased yet, and re-locking
    // an already LOCKED loan is a no-op the server would reject. Gated
    // on device.lock_override so the button isn't visible to roles that
    // would just 403.
    allowedFrom: ["ACTIVE", "IN_ARREARS"],
    permission: "device.lock_override",
    variant: "danger",
  },
  {
    label: "Unlock",
    kind: "Unlock",
    // Unlock dispatches a device command through the provider adapter
    // (see runTransition's branch). The server's POST /loans/:id/unlock
    // requires device.unlock_override, so gate the button on the SAME
    // permission — otherwise it renders for operators who'd just 403.
    allowedFrom: ["LOCKED"],
    permission: "device.unlock_override",
    variant: "primary",
  },
  {
    label: "Write off",
    kind: "WriteOff",
    needsReason: true,
    allowedFrom: [
      "PENDING_VERIFICATION",
      "PENDING_DEPOSIT",
      // A loan stuck on a never-enrolling device is a legitimate
      // write-off candidate — mirror the domain WRITE_OFF_ALLOWED_FROM
      // set so the button doesn't lie about what the backend will accept.
      "PENDING_DEVICE_PROVISIONING",
      "ACTIVE",
      "IN_ARREARS",
      "LOCKED",
      "IN_DISPUTE",
    ],
    permission: "loan.write_off",
    variant: "danger",
  },
];

export function LoanDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [reasonDraft, setReasonDraft] = useState("");
  const [activeKind, setActiveKind] = useState<LoanTransitionRequest["kind"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Lock form draft — distinct from the generic reason textarea because
  // POST /loans/:id/lock carries three correlated fields the server
  // validates as one body. Default to SOFT_LOCKED + stage 1 (the
  // gentlest manual action — escalating to HARD_LOCKED is one click
  // away once the customer's seen the soft-lock screen).
  const [lockDesired, setLockDesired] = useState<"SOFT_LOCKED" | "HARD_LOCKED">("SOFT_LOCKED");
  const [lockStage, setLockStage] = useState<number>(1);
  const [lockNote, setLockNote] = useState<string>("");
  // Undo affordance — after a successful Lock or Unlock, surface a
  // 30 s banner offering to reverse the action. Catches misclicks
  // that today require operator-clicks-back-to-loan to reverse.
  //
  // Shape:
  //   { kind: "Lock", expiresAtMs }    →  Undo button posts /unlock
  //   { kind: "Unlock", expiresAtMs }  →  Undo button posts /lock with
  //                                       the prior values
  //   null                              → no banner shown
  //
  // `expiresAtMs` drives the countdown displayed in the banner.
  const [undoToast, setUndoToast] = useState<{
    readonly kind: "Lock" | "Unlock";
    readonly expiresAtMs: number;
    readonly prior?: {
      readonly desired: "SOFT_LOCKED" | "HARD_LOCKED";
      readonly stage: number;
    };
  } | null>(null);
  // 1 Hz tick so the countdown displayed in the banner updates each
  // second. Tied to whether the banner is visible — no tick when
  // there's nothing to display.
  const [, forceTick] = useState(0);
  React.useEffect(() => {
    if (undoToast === null) return;
    const handle = window.setInterval(() => {
      if (Date.now() >= undoToast.expiresAtMs) {
        setUndoToast(null);
      } else {
        forceTick((n) => n + 1);
      }
    }, 1000);
    return () => window.clearInterval(handle);
  }, [undoToast]);

  // Live clock for the "Next payment" / "Lock time" countdowns — re-renders
  // once a second so the relative strings tick down without a refetch.
  const [nowMs, setNowMs] = useState(() => Date.now());
  React.useEffect(() => {
    const handle = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, []);

  const loanQuery = useQuery({
    queryKey: ["loans", "byId", id],
    queryFn: () => api.get<Loan>(`/api/loans/${id}`),
  });
  const loan = loanQuery.data;
  // Linked aggregates — load lazily once the loan resolves so a stale
  // link doesn't block the page. Each query is independent so a single
  // failure narrows the impact to its own card.
  const customerQuery = useQuery({
    queryKey: ["customers", "byId", loan?.customerId],
    queryFn: () => api.get<Customer>(`/api/customers/${loan?.customerId}`),
    enabled: Boolean(loan?.customerId),
  });
  const dealerQuery = useQuery({
    queryKey: ["dealers", "byId", loan?.dealerId],
    queryFn: () => api.get<Dealer>(`/api/dealers/${loan?.dealerId}`),
    enabled: Boolean(loan?.dealerId),
  });
  const deviceQuery = useQuery({
    queryKey: ["devices", "byId", loan?.deviceId],
    queryFn: () => api.get<Device>(`/api/devices/${loan?.deviceId}`),
    enabled: Boolean(loan?.deviceId),
  });
  // P2.C — keep the loan-detail page honest about device readiness.
  // When the loan is PENDING_DEVICE_PROVISIONING the operator needs
  // to see "is the device actually checking in?" without pivoting to
  // the device page. We poll every 15 s (matches the device page).
  const onboardingStatusQuery = useQuery({
    queryKey: ["onboarding-status", loan?.deviceId],
    queryFn: () =>
      api.get<import("@dfp/api-client").OnboardingStatus>(
        `/api/devices/${loan?.deviceId}/onboarding-status`,
      ),
    enabled: Boolean(loan?.deviceId),
    refetchInterval: 15_000,
  });
  const contractQuery = useQuery({
    queryKey: ["contracts", "byId", loan?.contractId],
    queryFn: () => api.get<Contract>(`/api/contracts/${loan?.contractId}`),
    // contractId can be present but a still-being-signed contract id —
    // skip when empty / sentinel.
    enabled: Boolean(loan?.contractId && loan.contractId.length > 0),
  });
  // Payments scoped to this loan, server-side by loanId. (Previously this fetched
  // the global — capped — payments list and filtered client-side, which silently
  // dropped this loan's payments once production had many.)
  const paymentsQuery = useQuery({
    queryKey: ["payments", "byLoan", id],
    queryFn: () => api.get<readonly Payment[]>(`/api/payments/loans/${id}`),
  });

  const transition = useMutation({
    mutationFn: (body: LoanTransitionRequest) =>
      api.post<Loan>(`/api/loans/${id}/transitions`, body),
    onSuccess: (next) => {
      queryClient.setQueryData<Loan>(["loans", "byId", id], next);
      void queryClient.invalidateQueries({ queryKey: ["loans", "list"] });
      setReasonDraft("");
      setActiveKind(null);
      setError(null);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Transition rejected");
      } else {
        setError("Network error");
      }
    },
  });

  // Unlock takes a different path from /transitions: the dedicated
  // /loans/:id/unlock route in lock-control invokes the provider adapter
  // (writes a device_command_queue row + dispatches FCM when a token is
  // on file), so the device actually receives the command. /transitions
  // updates loan.desiredLockState in the DB only — the DPC eventually
  // catches up via the lease piggy-back on its next snapshot, but the
  // operator-visible "click → device unlocks" loop is broken without
  // the adapter dispatch.
  //
  // The endpoint accepts an optional `note` (≥10 chars when present);
  // the current admin flow doesn't prompt for one on Unlock, so we send
  // an empty body. If we later add a reason prompt, route it through
  // here.
  //
  // Response shape: { loanId, desiredLockState, ... } from LockControlService
  // — NOT a full Loan row. We invalidate the loan query so the next
  // render fetches the fresh row instead of trying to patch the cache
  // from a partial payload.
  const unlock = useMutation({
    mutationFn: () => api.post<{ loanId: string }>(`/api/loans/${id}/unlock`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["loans", "byId", id] });
      void queryClient.invalidateQueries({ queryKey: ["loans", "list"] });
      setReasonDraft("");
      setActiveKind(null);
      setError(null);
      // Undo affordance — operator has 30 s to put the loan back into
      // the prior locked state. The "prior" values come from the
      // current loan row at the moment unlock landed (snapshotted on
      // the closure so a stale value can't leak). UNDO_WINDOW_MS is
      // the source of truth in the inline timer below.
      const prior = loan
        ? {
            desired: (loan.desiredLockState === "HARD_LOCKED" ? "HARD_LOCKED" : "SOFT_LOCKED") as
              | "SOFT_LOCKED"
              | "HARD_LOCKED",
            stage: loan.lockStage,
          }
        : undefined;
      setUndoToast({
        kind: "Unlock",
        expiresAtMs: Date.now() + UNDO_WINDOW_MS,
        ...(prior ? { prior } : {}),
      });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Unlock rejected");
      } else {
        setError("Network error");
      }
    },
  });

  // Manual lock — same dispatch shape as unlock. Server validates
  // (desired, stage, note≥10) as one body; we send what the form
  // captured.
  const lock = useMutation({
    mutationFn: () =>
      api.post<{ loanId: string }>(`/api/loans/${id}/lock`, {
        desired: lockDesired,
        stage: lockStage,
        ...(lockNote.trim().length >= 10 ? { note: lockNote.trim() } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["loans", "byId", id] });
      void queryClient.invalidateQueries({ queryKey: ["loans", "list"] });
      setLockNote("");
      setActiveKind(null);
      setError(null);
      setUndoToast({ kind: "Lock", expiresAtMs: Date.now() + UNDO_WINDOW_MS });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Lock rejected");
      } else {
        setError("Network error");
      }
    },
  });

  // Agent-triggered collection (USSD push). The agent picks the wallet +
  // optionally a phone to push to; the server creates an intent and asks the
  // provider to initiate. Settlement arrives later via the provider callback.
  const [collectAmount, setCollectAmount] = useState<string>("");
  const [collectPhone, setCollectPhone] = useState<string>("");
  const [collectMethod, setCollectMethod] = useState<string>("Mpesa");
  const [collectMsg, setCollectMsg] = useState<string | null>(null);
  const collect = useMutation({
    mutationFn: () =>
      api.post<{ initiation: { kind: string; instructions: string } }>(
        `/api/payments/loans/${id}/collect`,
        {
          amountMinor: Math.trunc(Number(collectAmount)),
          method: collectMethod,
          ...(collectPhone.trim() ? { phoneNumber: collectPhone.trim() } : {}),
        },
      ),
    onSuccess: (res) => {
      setError(null);
      setCollectMsg(res.initiation?.instructions ?? "Payment prompt sent to the customer.");
    },
    onError: (err: unknown) => {
      setCollectMsg(null);
      setError(
        err instanceof ApiError
          ? (err.problem.message ?? err.problem.detail ?? "Collection rejected")
          : "Network error",
      );
    },
  });

  // Issue a one-time unlock PIN (device.unlock_override). The operator reads
  // the returned code to a locked-out customer over the phone; the customer
  // redeems it on the lock screen for a temporary grace unlock. The code is
  // returned ONCE (only a salted hash is stored server-side), so it lives in
  // ephemeral state and is never cached or persisted.
  const [pinResult, setPinResult] = useState<{ code: string; expiresAt: string } | null>(null);
  const issuePin = useMutation({
    mutationFn: () =>
      api.post<{ code: string; expiresAt: string }>(`/api/loans/${id}/unlock-codes`, {}),
    onSuccess: (res) => {
      setError(null);
      setPinResult(res);
    },
    onError: (err: unknown) => {
      setPinResult(null);
      setError(
        err instanceof ApiError
          ? (err.problem.message ?? err.problem.detail ?? "Could not issue unlock PIN")
          : "Network error",
      );
    },
  });

  // Operator-issued queue-row overrides — force-ack / force-fail.
  // Both take the row id from the inline list rendered next to
  // pendingLockCommands. We keep one state for the reason draft + one
  // for the row + action being prompted; React Query handles cache
  // invalidation on success so the row disappears from the table the
  // next render.
  const [overrideRowId, setOverrideRowId] = useState<string | null>(null);
  const [overrideAction, setOverrideAction] = useState<"ack" | "fail" | null>(null);
  const [overrideReason, setOverrideReason] = useState<string>("");
  const closeOverride = (): void => {
    setOverrideRowId(null);
    setOverrideAction(null);
    setOverrideReason("");
  };
  const forceAck = useMutation({
    mutationFn: (vars: { rowId: string; reason: string }) =>
      api.post<{ rowId: string; newStatus: "ack" }>(
        `/api/loans/${id}/queue/${vars.rowId}/force-ack`,
        { reason: vars.reason },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["loans", "byId", id] });
      closeOverride();
      setError(null);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Force-ack rejected");
      } else {
        setError("Network error");
      }
    },
  });
  const forceFail = useMutation({
    mutationFn: (vars: { rowId: string; reason: string }) =>
      api.post<{ rowId: string; newStatus: "failed" }>(
        `/api/loans/${id}/queue/${vars.rowId}/force-fail`,
        { reason: vars.reason },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["loans", "byId", id] });
      closeOverride();
      setError(null);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Force-fail rejected");
      } else {
        setError("Network error");
      }
    },
  });

  if (loanQuery.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (loanQuery.isError || !loan) {
    return <p className="text-sm text-red-700">Failed to load loan.</p>;
  }
  const permitted = (perm: string): boolean => (auth.user?.permissions ?? []).includes(perm);
  const legalTransitions = TRANSITIONS.filter(
    (t) => t.allowedFrom.includes(loan.status) && permitted(t.permission),
  );

  function runTransition(t: TransitionDef): void {
    setError(null);
    // Unlock has its own dispatch path — see the `unlock` mutation comment.
    //
    // Confirm-on-destructive: a first click reveals an inline confirm
    // banner ("activeKind === Unlock"); the second click fires the
    // mutation. Catches the misclick where an operator hits Unlock on
    // a customer they meant to leave locked.
    if (t.kind === "Unlock") {
      if (activeKind !== "Unlock") {
        setActiveKind("Unlock");
        return;
      }
      unlock.mutate();
      return;
    }
    // Lock has its own form (3 correlated fields) + dedicated endpoint.
    // First click reveals the form; second click submits.
    if (t.kind === "Lock") {
      if (activeKind !== "Lock") {
        setActiveKind("Lock");
        return;
      }
      if (lockNote.trim().length > 0 && lockNote.trim().length < 10) {
        setError("Lock note must be at least 10 characters (or leave blank).");
        return;
      }
      lock.mutate();
      return;
    }
    if (t.needsReason) {
      if (activeKind !== t.kind) {
        setActiveKind(t.kind);
        setReasonDraft("");
        return;
      }
      if (reasonDraft.trim().length < 10) {
        setError("Reason must be at least 10 characters.");
        return;
      }
      transition.mutate({ kind: t.kind, reason: reasonDraft.trim() } as LoanTransitionRequest);
    } else {
      transition.mutate({ kind: t.kind } as LoanTransitionRequest);
    }
  }

  // Undo banner data (only render when we have an active toast).
  const undoSecondsLeft = undoToast
    ? Math.max(0, Math.ceil((undoToast.expiresAtMs - Date.now()) / 1000))
    : 0;
  const undoInverseLabel = undoToast?.kind === "Lock" ? "Unlock" : "Re-lock";
  const fireUndo = (): void => {
    if (!undoToast) return;
    if (undoToast.kind === "Lock") {
      // Inverse of Lock → Unlock.
      unlock.mutate();
    } else {
      // Inverse of Unlock → Lock with the prior values. When the
      // unlock landed without a `prior` (the loan was already loaded
      // post-unlock so we couldn't snapshot), we fall back to a
      // conservative SOFT_LOCKED + stage 1 to avoid escalating a
      // misclick into a HARD_LOCKED accident.
      const inv = undoToast.prior ?? { desired: "SOFT_LOCKED" as const, stage: 1 };
      // Mirror the lock mutation's body shape.
      void api
        .post<{ loanId: string }>(`/api/loans/${id}/lock`, {
          desired: inv.desired,
          stage: inv.stage,
        })
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: ["loans", "byId", id] });
          void queryClient.invalidateQueries({ queryKey: ["loans", "list"] });
          setUndoToast(null);
        })
        .catch(() => {
          /* error surfaces via the normal lock mutation path next time */
        });
    }
    setUndoToast(null);
  };
  return (
    <div className="space-y-6">
      {undoToast !== null ? (
        <div className="flex items-center justify-between rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
          <span>
            {undoToast.kind} succeeded — undo within{" "}
            <span className="font-mono">{undoSecondsLeft}s</span>?
          </span>
          <span className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border border-emerald-600 px-2 py-0.5 text-emerald-700 hover:bg-emerald-100"
              onClick={fireUndo}
            >
              {undoInverseLabel}
            </button>
            <button
              type="button"
              className="text-emerald-700/70 hover:text-emerald-700"
              onClick={() => setUndoToast(null)}
            >
              Dismiss
            </button>
          </span>
        </div>
      ) : null}
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Loan</h1>
        <p className="text-xs font-mono text-slate-500">{loan.id}</p>
        <p className="text-xs text-slate-500">
          Updated{" "}
          <span title={new Date(loan.updatedAt).toISOString()}>
            {new Date(loan.updatedAt).toLocaleString()}
          </span>
          {" · Created "}
          {new Date(loan.createdAt).toLocaleString()}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Related</CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 text-sm md:grid-cols-4">
          <CrossLink
            label="Customer"
            to="/customers/$id"
            id={loan.customerId}
            subtitle={
              customerQuery.data
                ? `${customerQuery.data.givenName} ${customerQuery.data.familyName}`
                : "—"
            }
          />
          <CrossLink
            label="Dealer"
            to="/dealers/$id"
            id={loan.dealerId}
            subtitle={dealerQuery.data?.name ?? "—"}
          />
          <CrossLink
            label="Device"
            to="/devices/$id"
            id={loan.deviceId}
            subtitle={
              deviceQuery.data
                ? `${deviceQuery.data.brand} ${deviceQuery.data.model} · IMEI ${deviceQuery.data.imei}${
                    // Snapshot freshness inline — operators on the loan
                    // page want to know "did this device check in
                    // recently?" without pivoting to the device page.
                    // The pure timestamp lives on the onboarding-status
                    // payload (lastSnapshotAt) — we shorten to "Last
                    // seen N min ago" so the row stays one line.
                    onboardingStatusQuery.data?.lastSnapshotAt
                      ? ` · last seen ${relativeFromNowShort(onboardingStatusQuery.data.lastSnapshotAt)}`
                      : onboardingStatusQuery.data &&
                          onboardingStatusQuery.data.lastSnapshotAt === null
                        ? " · never seen"
                        : ""
                  }`
                : "—"
            }
          />
          <CrossLink
            label="Contract"
            to="/contracts/$id"
            id={loan.contractId && loan.contractId.length > 0 ? loan.contractId : undefined}
            subtitle={
              contractQuery.data
                ? `${contractQuery.data.templateId} v${contractQuery.data.version}`
                : loan.contractId
                  ? "Loading…"
                  : "Not yet signed"
            }
          />
        </CardBody>
      </Card>

      {/*
        Service PIN — same gate as DeviceDetailPage: android-do devices
        only (the cheat targets the DPC) AND device.service_pin.view.
        Surfaced here too so an operator working a locked-out customer
        from the loan page doesn't have to pivot to the device page to
        read the service PIN. Uses the loan's deviceId (resolved via the
        device query for the control-provider check).
      */}
      {deviceQuery.data?.controlProvider === "android-do" &&
      permitted("device.service_pin.view") &&
      loan.deviceId ? (
        <ServicePinCard deviceId={loan.deviceId} />
      ) : null}

      {/*
        Offline unlock code — same android-do gate (the code only verifies
        against a DPC snapshot key) plus the unlock_code.issue permission. Lets
        support read a paid-but-offline customer their unlock code straight from
        the loan page, with the device status + last payment as evidence.
      */}
      {deviceQuery.data?.controlProvider === "android-do" && permitted("unlock_code.issue") ? (
        <OfflineUnlockCodeCard loanId={loan.id} currency={loan.principalCurrency} />
      ) : null}

      {contractQuery.isError && permitted("loan.write") ? (
        <Card>
          <CardHeader>
            <CardTitle>Contract — not yet signed</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <p className="text-slate-700">
              Capture both signatures + the GPS at signing to lock in the contract terms. The loan
              stays in <span className="font-medium">{loan.status}</span> until the dealer hands the
              device over.
            </p>
            <Link to="/loans/$id/sign-contract" params={{ id: loan.id }}>
              <Button>Sign contract…</Button>
            </Link>
          </CardBody>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
            <Badge
              tone={
                loan.status === "ACTIVE"
                  ? "success"
                  : loan.status === "LOCKED"
                    ? "danger"
                    : "neutral"
              }
            >
              {loan.status}
            </Badge>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <Row k="Lock stage" v={String(loan.lockStage)} />
            <Row k="Desired lock state" v={loan.desiredLockState} />
            <Row
              k="Provider lock state"
              v={renderProviderLockState(loan.providerLockState, loan.pendingLockCommands)}
            />
            {loan.status === "ACTIVE" ||
            loan.status === "IN_ARREARS" ||
            loan.status === "LOCKED" ? (
              <Row
                k="Next payment"
                v={`${formatDateTime(loan.nextEvalAt)} (${formatCountdown(
                  new Date(loan.nextEvalAt).valueOf(),
                  nowMs,
                )})`}
              />
            ) : null}
            {loan.lockTiming ? (
              <Row k="Lock time" v={formatLockTime(loan.lockTiming, nowMs)} />
            ) : null}
            {loan.lockTiming?.hardLockAt ? (
              <Row k="Hard lock" v={formatHardLock(loan.lockTiming.hardLockAt, nowMs)} />
            ) : null}
            {/*
              Pending lock commands — inline list with force-ack /
              force-fail buttons gated on device.lock_override. Renders
              only when there's at least one in-flight row; otherwise
              the operator's view stays clean.
            */}
            {loan.pendingLockCommands && loan.pendingLockCommands.length > 0 ? (
              <div className="space-y-2 border-t border-slate-100 pt-2">
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Pending commands
                </div>
                {loan.pendingLockCommands.map((row) => {
                  const showForm = overrideRowId === row.id;
                  const ageSec = Math.max(
                    0,
                    Math.floor((Date.now() - new Date(row.createdAt).valueOf()) / 1000),
                  );
                  const ageLabel =
                    ageSec < 60
                      ? `${ageSec}s ago`
                      : ageSec < 3600
                        ? `${Math.floor(ageSec / 60)}m ago`
                        : `${Math.floor(ageSec / 3600)}h ago`;
                  return (
                    <div
                      key={row.id}
                      className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-slate-600">
                          {row.kind}
                          {row.desired ? ` → ${row.desired}` : ""}
                        </span>
                        <span className="font-mono text-slate-500">{row.status}</span>
                        <span className="text-slate-400">{ageLabel}</span>
                        <span className="text-slate-300">·</span>
                        <span className="font-mono text-[10px] text-slate-400">
                          {row.id.slice(0, 8)}…
                        </span>
                        {permitted("device.lock_override") && !showForm ? (
                          <span className="ml-auto flex gap-1">
                            <button
                              type="button"
                              className="rounded border border-emerald-500 px-2 py-0.5 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => {
                                setOverrideRowId(row.id);
                                setOverrideAction("ack");
                                setOverrideReason("");
                              }}
                            >
                              Force ack
                            </button>
                            <button
                              type="button"
                              className="rounded border border-rose-500 px-2 py-0.5 text-rose-700 hover:bg-rose-50"
                              onClick={() => {
                                setOverrideRowId(row.id);
                                setOverrideAction("fail");
                                setOverrideReason("");
                              }}
                            >
                              Force fail
                            </button>
                          </span>
                        ) : null}
                      </div>
                      {showForm && overrideAction ? (
                        <div className="mt-2 space-y-2">
                          <textarea
                            className="w-full rounded border border-slate-300 p-2 font-sans text-xs"
                            placeholder={`Reason for force-${overrideAction} (10–500 chars, audit-logged)`}
                            rows={2}
                            maxLength={500}
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100"
                              onClick={closeOverride}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              disabled={
                                overrideReason.trim().length < 10 ||
                                forceAck.isPending ||
                                forceFail.isPending
                              }
                              className={`rounded px-2 py-1 text-white ${
                                overrideAction === "ack"
                                  ? "bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300"
                                  : "bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300"
                              }`}
                              onClick={() => {
                                const vars = { rowId: row.id, reason: overrideReason.trim() };
                                if (overrideAction === "ack") forceAck.mutate(vars);
                                else forceFail.mutate(vars);
                              }}
                            >
                              Confirm force-{overrideAction}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            <Row k="Device confirmed state" v={loan.deviceConfirmedState} />
            <Row k="Next eval" v={new Date(loan.nextEvalAt).toLocaleString()} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Money</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            {(() => {
              // Paid / overdue / balance from the per-installment status the
              // server annotates on the loan-detail response. "Overdue" only
              // shows when there genuinely is an overdue amount.
              const tot = (s: (typeof loan.schedule)[number]) =>
                s.principalMinor + s.interestMinor + s.feesMinor;
              const scheduled = loan.schedule.reduce((acc, s) => acc + tot(s), 0);
              // Sum the per-installment paid amounts (includes partials);
              // fall back to fully-paid installment totals for older server
              // builds that don't annotate paidMinor.
              const paid = loan.schedule.reduce(
                (acc, s) => acc + (s.paidMinor ?? (s.status === "paid" ? tot(s) : 0)),
                0,
              );
              const overdue = loan.schedule
                .filter((s) => s.status === "overdue")
                .reduce((acc, s) => acc + tot(s), 0);
              const balance = Math.max(0, scheduled - paid);
              // Next payment — the outstanding amount on the next installment
              // that isn't fully paid: a partial installment's remainder, or
              // the next upcoming installment's full amount.
              const nextDue = loan.schedule.find((s) => s.status !== "paid");
              const nextPaymentMinor = nextDue
                ? (nextDue.amountMinor ?? tot(nextDue)) - (nextDue.paidMinor ?? 0)
                : 0;
              return (
                <>
                  <Row k="Balance" v={formatMoney(balance, loan.principalCurrency)} />
                  <Row k="Paid" v={formatMoney(paid, loan.principalCurrency)} />
                  {overdue > 0 ? (
                    <Row k="Overdue" v={formatMoney(overdue, loan.principalCurrency)} />
                  ) : null}
                  {nextPaymentMinor > 0 ? (
                    <Row
                      k="Next payment"
                      v={formatMoney(nextPaymentMinor, loan.principalCurrency)}
                    />
                  ) : null}
                  <div className="space-y-2 border-t border-slate-100 pt-2 text-slate-500">
                    <Row
                      k="Principal"
                      v={formatMoney(loan.principalAmountMinor, loan.principalCurrency)}
                    />
                    <Row
                      k="Deposit"
                      v={formatMoney(loan.depositAmountMinor, loan.depositCurrency)}
                    />
                  </div>
                </>
              );
            })()}
          </CardBody>
        </Card>
      </div>

      {/*
        Repayment schedule — per-installment breakdown with a settlement
        status the server annotates on GET /api/loans/:id (paid /
        overdue / pending). Mirrors the customer app's PaymentsHistoryTab
        tone choices: paid → green, overdue → red, pending → neutral.
      */}
      <Collapsible
        title="Repayment schedule"
        subtitle={`${loan.schedule.length} installments`}
        defaultOpen={false}
      >
        <div className="border-t border-simuflex-line">
          {loan.schedule.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No schedule on this loan.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>#</Th>
                  <Th>Due</Th>
                  <Th>Principal</Th>
                  <Th>Interest</Th>
                  <Th>Fees</Th>
                  <Th>Total</Th>
                  <Th>Paid</Th>
                  <Th>Unpaid</Th>
                  <Th>Status</Th>
                </tr>
              </THead>
              <TBody>
                {loan.schedule.map((s, idx) => {
                  const total = s.amountMinor ?? s.principalMinor + s.interestMinor + s.feesMinor;
                  return (
                    <Tr key={s.installmentNumber ?? idx}>
                      <Td className="text-xs text-slate-500">{s.installmentNumber}</Td>
                      <Td className="text-xs text-slate-600">
                        {s.dueAt ? new Date(s.dueAt).toLocaleDateString() : "—"}
                      </Td>
                      <Td>{formatMoney(s.principalMinor, loan.principalCurrency)}</Td>
                      <Td>{formatMoney(s.interestMinor, loan.principalCurrency)}</Td>
                      <Td>{formatMoney(s.feesMinor, loan.principalCurrency)}</Td>
                      <Td className="font-medium">{formatMoney(total, loan.principalCurrency)}</Td>
                      <Td>
                        {s.paidMinor != null ? (
                          <span
                            className={
                              s.paidMinor > 0 ? "text-simuflex-forestGreen" : "text-slate-400"
                            }
                          >
                            {formatMoney(s.paidMinor, loan.principalCurrency)}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </Td>
                      <Td>
                        {(() => {
                          const unpaid = total - (s.paidMinor ?? 0);
                          return unpaid > 0 ? (
                            <span className="font-medium text-rose-700">
                              {formatMoney(unpaid, loan.principalCurrency)}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          );
                        })()}
                      </Td>
                      <Td>
                        {s.status ? (
                          <Badge tone={scheduleStatusTone(s.status)}>{s.status}</Badge>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </TBody>
            </Table>
          )}
        </div>
      </Collapsible>

      {permitted("payment.create_intent") &&
      loan.status !== "PAID_OFF" &&
      loan.status !== "WRITTEN_OFF" &&
      loan.status !== "REJECTED" ? (
        <Card>
          <CardBody>
            <h2 className="text-sm font-semibold text-slate-800">Request payment (USSD push)</h2>
            <p className="mt-1 text-xs text-slate-500">
              Push a mobile-money prompt to the customer for this loan. Leave phone blank to use
              their registered number.
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="flex flex-col text-xs text-slate-600">
                Amount (TZS)
                <input
                  type="number"
                  min={1}
                  value={collectAmount}
                  onChange={(e) => setCollectAmount(e.target.value)}
                  className="mt-1 w-32 rounded-md border border-slate-300 px-2 py-1"
                />
              </label>
              <label className="flex flex-col text-xs text-slate-600">
                Wallet
                <select
                  value={collectMethod}
                  onChange={(e) => setCollectMethod(e.target.value)}
                  className="mt-1 rounded-md border border-slate-300 px-2 py-1"
                >
                  <option value="Mpesa">M-Pesa</option>
                  <option value="TigoPesa">Mixx by Yas (Tigo)</option>
                  <option value="HaloPesa">HaloPesa (Halotel)</option>
                  <option value="AirtelMoney">Airtel Money</option>
                </select>
              </label>
              <label className="flex flex-col text-xs text-slate-600">
                Phone (optional)
                <input
                  value={collectPhone}
                  onChange={(e) => setCollectPhone(e.target.value)}
                  placeholder="registered number"
                  className="mt-1 w-40 rounded-md border border-slate-300 px-2 py-1"
                />
              </label>
              <Button
                onClick={() => collect.mutate()}
                disabled={collect.isPending || !(Number(collectAmount) > 0)}
              >
                {collect.isPending ? "Sending…" : "Request payment"}
              </Button>
            </div>
            {collectMsg ? <p className="mt-2 text-xs text-emerald-700">{collectMsg}</p> : null}
          </CardBody>
        </Card>
      ) : null}

      {/*
        Unlock PIN — for a customer locked out of their device. Gate on the
        device actually being locked (desiredLockState SOFT/HARD), not only
        on the LOCKED/IN_ARREARS lifecycle: an operator can drive
        desiredLockState to a locked state while the loan is still ACTIVE
        (a manual or test lock — see the lock control above, which writes
        desiredLockState only and leaves loan.status untouched), and that
        customer is just as locked out. The API (issueUnlockCode) permits
        any loan, so this UI gate was the only thing hiding the card.
      */}
      {permitted("device.unlock_override") &&
      (loan.status === "LOCKED" ||
        loan.status === "IN_ARREARS" ||
        loan.desiredLockState === "SOFT_LOCKED" ||
        loan.desiredLockState === "HARD_LOCKED") ? (
        <Card>
          <CardBody>
            <h2 className="text-sm font-semibold text-slate-800">Unlock PIN (read to customer)</h2>
            <p className="mt-1 text-xs text-slate-500">
              Generate a one-time code for a customer locked out of their device. Read it to them
              over the phone — they enter it on the lock screen for a temporary unlock (the loan
              stays in arrears and re-locks when the grace window lapses). The code is shown once
              and expires in 1 hour.
            </p>
            {pinResult ? (
              <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-xs font-medium text-emerald-700">
                  Read this code to the customer — it won&apos;t be shown again:
                </p>
                <p className="mt-1 font-mono text-3xl font-bold tracking-[0.3em] text-emerald-900">
                  {pinResult.code}
                </p>
                <p className="mt-1 text-xs text-emerald-700">
                  Expires {new Date(pinResult.expiresAt).toLocaleString()}
                </p>
                <div className="mt-3 flex gap-2">
                  <Button variant="secondary" onClick={() => setPinResult(null)}>
                    Done
                  </Button>
                  <Button onClick={() => issuePin.mutate()} disabled={issuePin.isPending}>
                    {issuePin.isPending ? "Issuing…" : "Issue another"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-3">
                <Button onClick={() => issuePin.mutate()} disabled={issuePin.isPending}>
                  {issuePin.isPending ? "Issuing…" : "Issue unlock PIN"}
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      ) : null}

      {loan.status === "PENDING_VERIFICATION" && permitted("loan.approve_verification") ? (
        <VerificationPanel
          loanId={loan.id}
          onDecided={(next) => {
            queryClient.setQueryData<Loan>(["loans", "byId", id], next);
            void queryClient.invalidateQueries({ queryKey: ["loans", "list"] });
          }}
        />
      ) : null}

      {/*
        Contract-required banner appears when the loan is PENDING_DEPOSIT
        and no contract row is on file. The deposit form below stays
        visible (operator can still capture the receipt) but its submit
        button disables when this is true; the link to the signing page
        is the unblocking action.
      */}
      {loan.status === "PENDING_DEPOSIT" && !loan.contractId ? (
        <ContractRequiredBanner loanId={loan.id} />
      ) : null}

      {/*
        P2.E.1 — PENDING_DEVICE_PROVISIONING explainer. The deposit has
        cleared (so the customer's money is in) but the device hasn't
        reported onboarding GREEN yet. Operationally the next step is
        almost always "go install / fix the DPC on the device"; the
        device link below jumps to the DeviceDetailPage where the
        OnboardingStatusCard shows the specific RED/AMBER reasons.
      */}
      {loan.status === "PENDING_DEVICE_PROVISIONING" ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="font-medium">Awaiting device provisioning</div>
          <p className="mt-1 text-amber-800">
            The deposit has cleared. The loan stays in this state until the DPC on the device
            reports onboarding GREEN (Device Owner granted, restrictions applied, fresh snapshot).
            No operator action is required here — installing / fixing the DPC on the device drives
            the flip to ACTIVE automatically on the next snapshot tick.
          </p>
          {loan.deviceId ? (
            <p className="mt-2">
              <Link
                to="/devices/$id"
                params={{ id: loan.deviceId }}
                className="font-medium text-amber-900 underline hover:text-amber-700"
              >
                View device onboarding status →
              </Link>
            </p>
          ) : null}
        </div>
      ) : null}

      {/*
        Payment recording card. Three sub-components, each self-gating:
          - RecordDepositForm     PENDING_DEPOSIT only, amount locked
          - RecordManualPaymentForm  ACTIVE / IN_ARREARS / LOCKED / etc.,
                                     free amount; hides on PENDING_DEPOSIT
          - LinkExistingPaymentForm  always available; search by ref
        So at any given time the operator sees the right combination
        for the current loan stage.
      */}
      <Card>
        <CardHeader>
          <CardTitle>Payments</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <RecordDepositForm
            loanId={loan.id}
            loanStatus={loan.status}
            depositAmountMinor={loan.depositAmountMinor}
            depositCurrency={loan.depositCurrency}
            contractMissing={!loan.contractId}
          />
          <RecordManualPaymentForm loanId={loan.id} loanStatus={loan.status} />
          <LinkExistingPaymentForm loanId={loan.id} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payments</CardTitle>
          <span className="text-xs text-slate-500">{paymentsQuery.data?.length ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {paymentsQuery.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : paymentsQuery.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load payments.</p>
          ) : (paymentsQuery.data ?? []).length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No payments for this loan.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>ID</Th>
                  <Th>Channel</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Settled</Th>
                </tr>
              </THead>
              <TBody>
                {(paymentsQuery.data ?? []).map((p) => (
                  <Tr key={p.id}>
                    <Td>
                      <span className="font-mono text-xs text-slate-600">{p.id.slice(0, 8)}…</span>
                    </Td>
                    <Td className="text-sm">{p.channel}</Td>
                    <Td>{formatMoney(p.amountMinor, p.currency)}</Td>
                    <Td>
                      <Badge
                        tone={
                          p.status === "settled"
                            ? "success"
                            : p.status === "failed"
                              ? "danger"
                              : "neutral"
                        }
                      >
                        {p.status}
                      </Badge>
                    </Td>
                    <Td className="text-xs text-slate-600">
                      {p.settledAt ? new Date(p.settledAt).toLocaleString() : "—"}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Transitions</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          {(() => {
            const routine = legalTransitions.filter((t) => t.variant !== "danger");
            const destructive = legalTransitions.filter((t) => t.variant === "danger");
            const canRequestApproval =
              loan.status !== "PAID_OFF" &&
              loan.status !== "WRITTEN_OFF" &&
              loan.status !== "REJECTED" &&
              loan.status !== "DRAFT" &&
              (auth.user?.permissions.includes("iam.approval.create") ?? false);
            if (routine.length === 0 && destructive.length === 0 && !canRequestApproval) {
              return (
                <p className="text-sm text-slate-500">
                  No transitions available from status {loan.status}.
                </p>
              );
            }
            const hasDangerGroup = destructive.length > 0 || canRequestApproval;
            return (
              <div className="flex flex-wrap items-center gap-2">
                {routine.map((t) => (
                  <Button
                    key={t.kind}
                    variant={t.variant ?? "secondary"}
                    onClick={() => runTransition(t)}
                    disabled={transition.isPending || unlock.isPending || lock.isPending}
                  >
                    {t.label}
                  </Button>
                ))}
                {routine.length > 0 && hasDangerGroup ? (
                  <span aria-hidden className="mx-1 h-6 w-px self-center bg-slate-200" />
                ) : null}
                {destructive.map((t) => (
                  <Button
                    key={t.kind}
                    variant="danger"
                    onClick={() => runTransition(t)}
                    disabled={transition.isPending || unlock.isPending || lock.isPending}
                  >
                    {t.label}
                  </Button>
                ))}
                {canRequestApproval ? (
                  <RequestApprovalForm
                    permissionKey="loan.write_off"
                    label="write-off loan"
                    variant="danger"
                    payloadBuilder={(reason) => ({ loanId: loan.id, reason })}
                  />
                ) : null}
              </div>
            );
          })()}

          {activeKind && activeKind !== "Lock" ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-700">
                  Reason for {activeKind} (≥ 10 chars)
                </span>
                <textarea
                  value={reasonDraft}
                  onChange={(e) => setReasonDraft(e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </label>
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setActiveKind(null);
                    setReasonDraft("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    runTransition(
                      legalTransitions.find((t) => t.kind === activeKind) as TransitionDef,
                    )
                  }
                  disabled={transition.isPending || reasonDraft.trim().length < 10}
                >
                  Confirm {activeKind}
                </Button>
              </div>
            </div>
          ) : null}

          {activeKind === "Lock" ? (
            <div
              data-testid="lock-form"
              className="rounded-md border border-red-200 bg-red-50 p-3 space-y-3"
            >
              <p className="text-xs text-red-900">
                Manual lock bypasses the auto-cascade. Use for stolen-device reports, customer
                disputes that need an immediate hold, or fraud signals — not for routine arrears.
              </p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Desired state</span>
                  <div className="mt-1 flex gap-3 text-sm">
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="radio"
                        name="lockDesired"
                        value="SOFT_LOCKED"
                        checked={lockDesired === "SOFT_LOCKED"}
                        onChange={() => setLockDesired("SOFT_LOCKED")}
                      />
                      SOFT_LOCKED
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="radio"
                        name="lockDesired"
                        value="HARD_LOCKED"
                        checked={lockDesired === "HARD_LOCKED"}
                        onChange={() => setLockDesired("HARD_LOCKED")}
                      />
                      HARD_LOCKED
                    </label>
                  </div>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Stage (1–7)</span>
                  <input
                    type="number"
                    min={1}
                    max={7}
                    value={lockStage}
                    onChange={(e) => setLockStage(Number(e.target.value) || 1)}
                    className="mt-1 block w-24 rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-slate-700">
                  Note (optional, ≥ 10 chars if present)
                </span>
                <textarea
                  value={lockNote}
                  onChange={(e) => setLockNote(e.target.value)}
                  rows={2}
                  placeholder="e.g. customer reported phone stolen 2026-06-03; ref STOLEN-2841"
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </label>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setActiveKind(null);
                    setLockNote("");
                    setLockStage(1);
                    setLockDesired("SOFT_LOCKED");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() =>
                    runTransition(legalTransitions.find((t) => t.kind === "Lock") as TransitionDef)
                  }
                  disabled={lock.isPending}
                >
                  Confirm Lock
                </Button>
              </div>
            </div>
          ) : null}

          {/*
            Unlock confirm — symmetrical to Lock. First click on the
            Unlock button sets activeKind = "Unlock" (in runTransition);
            this panel renders the affirm/cancel pair. Without this an
            operator clicking Unlock by mistake fired the API
            immediately + had to chase down the inverse manually.
          */}
          {activeKind === "Unlock" ? (
            <div
              data-testid="unlock-confirm"
              className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3"
            >
              <p className="text-sm text-amber-900">
                Unlock this loan? The device will be re-enabled within ~30 s of the next FCM push.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setActiveKind(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() =>
                    runTransition(
                      legalTransitions.find((t) => t.kind === "Unlock") as TransitionDef,
                    )
                  }
                  disabled={unlock.isPending}
                >
                  Confirm Unlock
                </Button>
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * Tone for a schedule installment's settlement status. Mirrors the
 * customer app's PaymentsHistoryTab: paid → green, overdue → red,
 * pending → neutral.
 */
function scheduleStatusTone(
  status: "pending" | "paid" | "partial" | "overdue",
): "success" | "warning" | "danger" | "neutral" {
  switch (status) {
    case "paid":
      return "success";
    case "partial":
      return "warning";
    case "overdue":
      return "danger";
    case "pending":
      return "neutral";
  }
}

/** "Jun 23, 2026, 2:00 PM" — compact absolute date+time in the operator's locale. */
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** Relative countdown to/from a target: "in 3d 2h", "in 12m 5s", "now", "3d ago". */
function formatCountdown(targetMs: number, nowMs: number): string {
  const diff = targetMs - nowMs;
  const abs = Math.abs(diff);
  if (abs < 1000) return "now";
  const d = Math.floor(abs / 86_400_000);
  const h = Math.floor((abs % 86_400_000) / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  const s = Math.floor((abs % 60_000) / 1000);
  const parts = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  return diff > 0 ? `in ${parts}` : `${parts} ago`;
}

/**
 * Contextual lock-time string: when locked, "Locked since <date> (<ago>)";
 * when running, the deadline "<date> (locks in <countdown>)"; "—" otherwise.
 */
function formatLockTime(lt: NonNullable<Loan["lockTiming"]>, nowMs: number): string {
  if (lt.locked) {
    if (!lt.lockedAt) return "Locked";
    return `Locked since ${formatDateTime(lt.lockedAt)} (${formatCountdown(
      new Date(lt.lockedAt).valueOf(),
      nowMs,
    )})`;
  }
  if (lt.lockDeadlineAt) {
    const deadlineMs = new Date(lt.lockDeadlineAt).valueOf();
    const rel = formatCountdown(deadlineMs, nowMs);
    // A future deadline reads "locks in 5h"; a past one — the device should
    // have locked but is still unlocked — reads "was due to lock 1h ago".
    const phrase = deadlineMs > nowMs ? `locks ${rel}` : `was due to lock ${rel}`;
    return `${formatDateTime(lt.lockDeadlineAt)} (${phrase})`;
  }
  return "—";
}

/**
 * "Hard lock" row, shown only while SOFT_LOCKED — when the cascade escalates to
 * a full HARD_LOCKED. Future reads "hard-locks in 2d"; past (the escalation is
 * due but hasn't applied yet, e.g. the device is offline) reads "was due to
 * hard-lock 1h ago".
 */
function formatHardLock(hardLockAt: string, nowMs: number): string {
  const atMs = new Date(hardLockAt).valueOf();
  const rel = formatCountdown(atMs, nowMs);
  const phrase = atMs > nowMs ? `hard-locks ${rel}` : `was due to hard-lock ${rel}`;
  return `${formatDateTime(hardLockAt)} (${phrase})`;
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs uppercase tracking-wide text-slate-500">{k}</span>
      <span className="text-sm font-medium text-slate-900">{v}</span>
    </div>
  );
}

/**
 * Render the loan's providerLockState with awareness of in-flight
 * commands. The android-do provider returns UNKNOWN from applyCommand
 * synchronously, which the bare label rendered indistinguishably from
 * "we genuinely have no information." Edge cases:
 *
 *   - UNKNOWN + 1 in-flight command       → "Awaiting device confirmation
 *                                            (SOFT_LOCKED · queued)"
 *   - UNKNOWN + N>1 in-flight commands    → "Awaiting (N pending; newest:
 *                                            HARD_LOCKED · sent)" — happens
 *                                            after rapid operator clicks
 *                                            now that 0032 lets fresh rows
 *                                            land.
 *   - UNKNOWN + 0 in-flight commands      → bare "UNKNOWN" — genuinely no
 *                                            info, e.g. brand-new loan.
 *   - any non-UNKNOWN value               → render verbatim. We still
 *                                            mention if a command is in
 *                                            flight ("UNLOCKED · pending
 *                                            HARD_LOCKED") so the
 *                                            disagreement is visible.
 *   - pendingLockCommands field absent    → older server build; fall back
 *                                            to the bare value.
 */
function renderProviderLockState(state: string, pending: Loan["pendingLockCommands"]): string {
  if (!pending || pending.length === 0) return state;
  // Server returns commands newest-first (see listInFlightForDevice
  // ORDER BY created_at DESC). pending[0] therefore exists here, but
  // narrow defensively in case a future server build changes the
  // contract.
  const newest = pending[0];
  if (!newest) return state;
  const target = newest.desired ?? newest.kind;
  if (state === "UNKNOWN") {
    return pending.length === 1
      ? `Awaiting device confirmation (${target} · ${newest.status})`
      : `Awaiting (${pending.length} pending; newest: ${target} · ${newest.status})`;
  }
  return `${state} · pending ${target}`;
}

function CrossLink({
  label,
  to,
  id,
  subtitle,
}: {
  label: string;
  to: "/customers/$id" | "/dealers/$id" | "/devices/$id" | "/contracts/$id";
  id: string | undefined;
  subtitle: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
        {id ? (
          <Link
            to={to}
            params={{ id }}
            className="font-mono text-xs text-brand-600 hover:underline"
          >
            {id.slice(0, 8)}…
          </Link>
        ) : (
          <Badge tone="neutral">—</Badge>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-700">{subtitle}</p>
    </div>
  );
}

/**
 * Compact "5m ago" / "2h ago" / "3d ago" relative-time formatter for
 * inline subtitle use. We avoid Intl.RelativeTimeFormat here because
 * the device-row subtitle is constrained on width.
 */
function relativeFromNowShort(iso: string): string {
  const ms = Date.now() - new Date(iso).valueOf();
  if (ms < 0) return "just now";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
